import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
   prompt: () => Promise<void>;
   userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
   readonly canInstall$ = new BehaviorSubject(false);
   readonly isInstalled$ = new BehaviorSubject(false);

   private deferredPrompt: BeforeInstallPromptEvent | null = null;

   constructor() {
      if (typeof window === 'undefined') {
         return;
      }

      this.isInstalled$.next(this.isInstalled());
      this.registerServiceWorker();
      this.listenInstallEvents();
   }

   initialize(): void {
      this.isInstalled$.next(this.isInstalled());
   }

   isInstalled(): boolean {
      if (typeof window === 'undefined') {
         return false;
      }

      return (
         window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches ||
         (window.navigator as Navigator & { standalone?: boolean }).standalone === true
      );
   }

   isIos(): boolean {
      if (typeof navigator === 'undefined') {
         return false;
      }

      return /iPad|iPhone|iPod/.test(navigator.userAgent);
   }

   isAndroid(): boolean {
      if (typeof navigator === 'undefined') {
         return false;
      }

      return /Android/i.test(navigator.userAgent);
   }

   getInstructionsKey(): string {
      if (this.isIos()) {
         return 'InstallWebAppIosInstructions';
      }

      if (this.isAndroid()) {
         return 'InstallWebAppAndroidInstructions';
      }

      return 'InstallWebAppDesktopInstructions';
   }

   hasInstallPrompt(): boolean {
      return !!this.deferredPrompt;
   }

   async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
      if (!this.deferredPrompt) {
         return 'unavailable';
      }

      try {
         await this.deferredPrompt.prompt();
         const { outcome } = await this.deferredPrompt.userChoice;
         this.deferredPrompt = null;
         this.canInstall$.next(false);

         if (outcome === 'accepted') {
            this.isInstalled$.next(true);
         }

         return outcome;
      } catch {
         this.deferredPrompt = null;
         this.canInstall$.next(false);
         return 'unavailable';
      }
   }

   registerServiceWorker(): void {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
         return;
      }

      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
   }

   private listenInstallEvents(): void {
      window.addEventListener('beforeinstallprompt', (event) => {
         event.preventDefault();
         this.deferredPrompt = event as BeforeInstallPromptEvent;
         this.canInstall$.next(true);
      });

      window.addEventListener('appinstalled', () => {
         this.deferredPrompt = null;
         this.canInstall$.next(false);
         this.isInstalled$.next(true);
      });
   }
}
